import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { MarketProgram } from '@/features/bidding/constants'
import { CASCADE_MARKETS } from '@/features/bidding/constants'
import type { CompletedBidMarkets } from '@/features/bidding/endpoints/completedBidMarkets'
import {
  GET_COMPLETED_BID_MARKETS_API_ID,
  getCompletedBidMarkets,
} from '@/features/bidding/endpoints/completedBidMarkets'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

export type UseCompletedBidMarketsQueryParams = {
  deliveryDay: MarketDate
  portfolioCode: string
  resourceCategory: string
}

export type UseCompletedBidMarketsQueryResult = Omit<UseQueryResult, 'data'> & {
  completedBidMarkets: CompletedBidMarkets | null
  bidStatus: Record<CascadeMarketId, boolean>
}

/**
 * Transforms the API response into a Record<CascadeMarketId, boolean>.
 * A cascade market is considered "completed" if any of its underlying market programs has a bid.
 */
function transformToBidStatus(completedMarkets: CompletedBidMarkets | null): Record<CascadeMarketId, boolean> {
  const bidStatus: Record<CascadeMarketId, boolean> = {
    mfrr: false,
    'da-wholesale': false,
    'fcr-d': false,
    'fcr-n': false,
  }

  if (!completedMarkets) {
    return bidStatus
  }

  // Create a set of market programs that have bids for quick lookup
  const marketProgramsWithBids = new Set(completedMarkets.markets.map((m) => m.marketProgram))

  // Check each cascade market to see if any of its market programs has a bid
  CASCADE_MARKETS.forEach((cascadeMarket) => {
    const hasAnyBid = cascadeMarket.marketPrograms.some((program) =>
      marketProgramsWithBids.has(program as MarketProgram),
    )
    bidStatus[cascadeMarket.id] = hasAnyBid
  })

  return bidStatus
}

export function useCompletedBidMarketsQuery(
  params: UseCompletedBidMarketsQueryParams,
  options?: Omit<UseQueryOptions<CompletedBidMarkets>, 'queryKey' | 'queryFn'>,
): UseCompletedBidMarketsQueryResult {
  const { deliveryDay, portfolioCode, resourceCategory } = params

  const { data = null, ...queryResult } = useQuery({
    ...options,
    queryKey: [GET_COMPLETED_BID_MARKETS_API_ID, deliveryDay.toISODate(), portfolioCode, resourceCategory],
    queryFn: () =>
      getCompletedBidMarkets({
        deliveryDay: deliveryDay.toISODate(),
        portfolioCode,
        resourceCategory,
      }),
  })

  useErrorHandler(queryResult.isError)

  const bidStatus = useMemo(() => transformToBidStatus(data), [data])

  return { completedBidMarkets: data, bidStatus, ...queryResult }
}

export function useInvalidateCompletedBidMarketsQuery() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [GET_COMPLETED_BID_MARKETS_API_ID] })
}

export const useIsFetchingCompletedBidMarkets = (): boolean =>
  useIsFetching({ queryKey: [GET_COMPLETED_BID_MARKETS_API_ID] }) > 0
